import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { RoutePaths } from "../../../../enumStrings";
import { getEventTicketSalesRows, type EventTicketSalesTypeLine } from "./eventTicketSalesRows";
import {
  formatEventTicketTypeLinesForCsv,
  getEventAttendeeTicketGroupHeaders,
  getEventRegistrationTicketTypeLines,
} from "./eventAttendeeTicketGroups";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { TableEmptyState } from "../../../common/dashboard/TableEmptyState";
import {
  createInvoiceButtonSx,
  PreviewInvoiceButton,
} from "../../../common/dashboard/InvoiceFormControls";
import {
  CmpEventRegistrationFieldsFragment,
  CmpEventRegistrationStatusEnum,
  CmpEventTicketAudienceEnum,
  CmpInvoiceActionTypeEnum,
  CmpInvoiceDocumentEmailModeEnum,
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
  CmpOperationalSourceKindEnum,
  useCreateCmpEventRegistrationMutation,
  useSaveCmpEventInvoiceDraftMutation,
  useExportCmpEventAttendeesCsvLazyQuery,
  useEmailCmpInvoiceDocumentMutation,
  useGetCmpEventDetailQuery,
  useGetCmpEventRegistrationsQuery,
  useGetCmpEventSponsorshipRequestsQuery,
  useGetCmpEventTicketInvoiceDraftsQuery,
  useGetCmpInvoiceDetailQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useGetCmpManageEventsQuery,
  useRecordCmpEventRegistrationManualPaymentMutation,
  useUpdateCmpEventRegistrationMutation,
} from "../../../../graphql/types";
import {
  getEventInvoiceDraftSavedMessage,
  parseEventInvoiceDraftForm,
  serializeEventInvoiceDraftForm,
  toEventInvoiceDraftLineInputs,
  useEventInvoiceDraftFromRoute,
} from "./eventInvoiceDrafts";
import { useCmpGlobalSettings } from "../../../../hooks/useCmpSettingsSection";
import { scrollDashboardToTop } from "../../../../hooks/useScrollOnTabChange";
import { scrollToFirstInvalidField } from "../../../common/dashboard/scrollToFirstInvalidField";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import {
  stickyActionColumnCellSx,
  stickyActionColumnHeaderSx,
} from "../../../common/dashboard/DataTable";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import { hasUnsavedFormChanges } from "../../../common/formStateDirty";
import UnsavedChangesPrompt, {
  UnsavedChangesDialog,
} from "../../../common/UnsavedChangesPrompt";
import InvoiceCreatedDialog from "../../Invoicing/InvoiceCreatedDialog";
import { refreshAfterConfirmedSave } from "../../Invoicing/refreshAfterConfirmedSave";
import { useEventSalesReadback } from "./eventSalesReadback";
import { invoicePageLayoutPresentation } from "../../Invoicing/invoicePageHeaderPresentation";
import { InvoiceDesignScope } from "../../Invoicing/InvoiceDesignSystem";
import { isReceiptEligiblePaymentStatus } from "../../Invoicing/invoiceFormatting";
import { getSavedInvoiceRecipientEmail } from "../../Invoicing/invoiceDeliveryFeedback";
import {
  getStaffInvoiceDraftPaymentMethods,
  InvoiceSuccessDialog,
  PaymentProcessedDialog,
} from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { sortRowsByInvoiceNumber } from "../../Invoicing/ManageInvoices/invoiceNumberSorting";
import ManageInvoiceActionConfirmationDialog from "../../Invoicing/ManageInvoices/ManageInvoiceActionConfirmationDialog";
import SavedEventInvoiceDocumentModal from "../../Invoicing/ManageInvoices/SavedEventInvoiceDocumentModal";
import { canManageEventInvoiceFinancialActions } from "../../Invoicing/ManageInvoices/manageEventInvoicePermissions";
import {
  CMP_INVOICE_CREATE_LOOKUP_LIMIT,
  MEMBER_ONLY_INVOICE_LOOKUP_PARTS,
} from "../../Invoicing/invoiceLookupConfig";
import {
  CmpEventSalesRouteTarget,
  getCmpEventSalesRouteTarget,
} from "../../Invoicing/invoiceCardCheckoutRouting";
import {
  BillingDetails,
  MemberBillingMode,
  ConfirmDialog,
  blackButtonSx,
  blankBillingDetails,
  cpaGold,
  downloadCsvFile,
  downloadEventAttendeeListCsv,
  eventCardPaymentReceiptUnavailableMessage,
  EventSalesFilterBar,
  EventSalesSection,
  EventSalesSortableHeader,
  EventSalesSortState,
  EventSelect,
  EventStatCardRow,
  formatCurrencyFromCents,
  getConfiguredInvoiceTaxRateLabel,
  getEventInvoiceDisplayName,
  getConfiguredInvoiceTaxRatePercentage,
  goldButtonSx,
  salesActionCellButtonsSx,
  goldFieldSx,
  isComplimentaryEventPaymentStatus as isComplimentaryStatus,
  isPaidEventPaymentStatus as isPaidStatus,
  MemberBillingSelector,
  MemberLookupOption,
  ProcessPaymentModal,
  ProcessPaymentSubmit,
  ResultModal,
  splitName,
  toCmpEventBillingInput,
  toCmpEventBillingInputFromSnapshot,
  toggleEventSalesSort,
  UpdateAttendeeNamesModal,
  useDownloadEventInvoicePdf,
} from "./eventSalesShared";
import {
  getStaffPaymentMethodOptions,
  useStaffRecordablePaymentMethods,
} from "../../Invoicing/staffPaymentMethods";
import EventDashboardPixelQa, {
  getDashboardPixelQaState,
} from "../pixelQa/EventDashboardPixelQa";
import {
  prepareEventSalesOperation,
  completeEventSalesOperation,
  runEventSalesOperation,
  runEventSalesSingleFlight,
} from "./eventSalesOperation";
import { validateEventSalesBilling } from "./eventSalesBilling";
import {
  buildStaffCheckFirstFallback,
  buildStaffErrorFallback,
  getCmpErrorReason,
  getGraphQLErrorMessage,
  getUserFacingErrorText,
  hasGraphQLErrorCode,
  UserFacingError,
} from "../../../../utils/userFacingError";
import EventSecureCardCheckoutPanel from "./EventSecureCardCheckoutPanel";
import { DATE_FORMAT_MESSAGE } from "../../../../utils/fieldMessages";
import {
  EVENT_DRAFT_NOT_READ_TEXT,
  EVENT_INVOICE_NOT_FOUND_TEXT,
  EVENT_INVOICE_NOTHING_TO_PAY_TEXT,
} from "./eventSalesText";
import { getEventSecureCardCheckoutReadback } from "./eventSecureCardCheckout";
import {
  CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE,
  eventSalesCallerHandledApolloContext,
  eventSalesExportApolloContext,
  eventSalesHostedCheckoutApolloContext,
  eventSalesRegistrationCreateApolloContext,
} from "./eventSalesApolloErrorPolicy";
import {
  buildCmpEventAttendeeNameUpdateInputs,
  buildGuardedEventAttendeeUpdateInput,
  getEventSponsorshipAttendeeCount,
  getEventRegistrationTicketCount,
  getEventRegistrationReservedAttendeeCount,
  toCmpEventCustomFieldAnswerInputs,
} from "./eventSalesAttendeeUpdates";
import type { EventSalesAttendeeNameEdit } from "./eventSalesAttendeeUpdates";
import EventInvoiceDraftPreview from "./EventInvoiceDraftPreview";
import {
  getEventAttendeeCompanyOptions,
  useEventMemberAttendeeSuggestions,
} from "./useEventMemberAttendeeSuggestions";
import {
  getEventTicketDraftAttendeeCount,
  getEventTicketStatsCards,
} from "./eventTicketSalesStats";
import {
  eventInvoiceSelectableStatusFilterOptions,
  EventInvoiceSelectableStatusFilter,
  eventSaleMatchesStatusFilters,
  getEventInvoiceDocumentMode,
  getEventSaleInvoiceStatusLabel,
} from "./eventInvoiceStatus";
import type { EventSalesPageMode } from "./eventSalesPageMode";
import {
  buildEventFilteredAttendeeCsv,
  eventFilteredAttendeeListCsvFilename,
  eventSalesChangesSavedMessage,
  getEventAttendeeListExportSuccess,
  getEventAttendeeUpdateHelperText,
  getEventFilteredAttendeeListExportSuccess,
  type EventSalesSuccessMessage,
} from "./eventSalesExports";
import {
  getEventSalesAttendeeEditTarget,
  getSponsorshipSalesSearch,
  type EventSalesAttendeeEditTarget,
} from "./eventSalesAttendeeEditRoute";
import {
  getEventPaymentSuccessDetails,
  type EventPaymentSuccessDetails,
} from "./eventPaymentSuccess";
import EventInvoiceCreateFields from "./EventInvoiceCreateFields";
import {
  emailSavedEventInvoiceDocument,
  type PendingEventInvoiceDocumentEmail,
} from "./eventInvoiceDocumentEmail";
import EventTicketInvoiceLinesEditor from "./EventTicketInvoiceLinesEditor";
import EventTicketTaxExemptBox from "./EventTicketTaxExemptBox";
import { getEventAttendeeChoiceFields } from "../eventBackendMappers";
import {
  getEventAttendeeSuggestions,
  mergeEventAttendeeSuggestions,
} from "./eventAttendeeSuggestions";
import EventSalesSearchableMultiSelectMenu from "./EventSalesSearchableMultiSelectMenu";
import {
  createEventTicketInvoiceLine,
  getDefaultEventInvoiceDates,
  getStaffTicketEventEligibility,
  getStaffTicketNonMemberBlockCause,
  getStaffTicketNonMemberBlockCauseFromReason,
  getStaffTicketNonMemberBlockedMessage,
  CMP_STAFF_TICKET_REFUSAL_REASONS,
  getEventTicketLinePricing,
  getEventInvoiceDueDateForIssueDate,
  getEventInvoiceAttendeeCustomFieldAnswerInputs,
  getEventInvoiceTaxAmountInCents,
  getEventTicketTaxableSubtotalInCents,
  getEventTicketTaxNotAppliedNote,
  getEventTicketInvoiceDocumentLines,
  hasDuplicateEventTicketTypes,
  isEventInvoiceDateRangeValid,
  isEventInvoiceDateValid,
  normalizeEventInvoiceReference,
  parseEventTicketDiscountAmountInCents,
  parseEventTicketDiscountPercentage,
  getEventInvoicePrimaryAttendeeId,
  validateEventInvoiceAttendeeFields,
  type EventTicketInvoiceLine,
} from "./eventInvoiceCreateState";
import {
  getTicketInvoiceFormDirtyState,
  shouldConfirmEventSelectionChange,
  shouldRebaseEventInvoiceDateDefaults,
  TicketInvoiceFormDirtyState,
} from "./eventSalesInvoiceFormDirtyState";

type Registration = CmpEventRegistrationFieldsFragment;

/** Monday D30: the Create Ticket Invoice form kept in a saved draft. */
type TicketInvoiceDraftForm = {
  version: number;
  eventId: string;
  billingMode: MemberBillingMode;
  billing: BillingDetails;
  ticketLines: EventTicketInvoiceLine[];
  invoiceDate: string;
  dueDate: string;
  referencePurchaseOrder: string;
  taxExempt: boolean;
  taxExemptionNumber: string;
};

type SortColumn =
  | "invoiceNumber"
  | "attendeeName"
  | "companyName"
  | "invoiceStatus"
  | "ticketType"
  | "tickets"
  | "ticketStatus";

const ticketStatusLabel = (registration: Registration) =>
  isPaidStatus(registration.paymentStatus) ? "Issued" : "Allocated";

// Ticket Type cell (design D:4681-4685): one line per ticket type with a grey
// "× qty". Sponsorship rows have one line and no quantity.
const TicketTypeLinesCell = ({
  lines,
}: {
  lines: readonly EventTicketSalesTypeLine[];
}): React.ReactElement => (
  <div className="flex min-w-0 flex-col gap-[4px]" data-ticket-type-lines>
    {lines.length === 0 ? "—" : null}
    {lines.map((line, index) => (
      <div
        className="flex items-baseline gap-[6px] text-[14px] text-[#0A0A0A]"
        data-ticket-type-line
        key={`${line.label}-${index}`}
      >
        <span>{line.label || "—"}</span>
        {/* D:1708840 (EV15-84): "\u00d7 n" only when the invoice has several ticket types. */}
        {line.quantity != null && lines.length > 1 ? (
          <span className="whitespace-nowrap text-[13px] text-[#727272]">
            {`\u00d7 ${line.quantity}`}
          </span>
        ) : null}
      </div>
    ))}
  </div>
);


// A line with no attendees yet (quantity 0). Only attendee 1 of the invoice
// needs an email (Moeen, Sept 24 2026).
const eventInvoiceAttendeesRequiredMessage =
  "Enter a quantity of at least 1, then add the attendee details.";

const isNonMemberInvoiceTicketType = (audience: CmpEventTicketAudienceEnum) =>
  audience !== CmpEventTicketAudienceEnum.ActiveMembers &&
  audience !== CmpEventTicketAudienceEnum.SponsorIncluded;

// Design D:3287-3289: shown above Preview/Create when a line has no type.
const eventTicketTypeRequiredMessage =
  "Please select a ticket type for each line before creating the invoice.";

// Design D:4583 and D:13294: gold while a filter is active, grey otherwise.
const eventFilteredExportButtonSx = (enabled: boolean) =>
  ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    ...(enabled
      ? {}
      : {
          backgroundColor: "#9E9E9E",
          cursor: "not-allowed",
          "&:hover": { backgroundColor: "#9E9E9E", boxShadow: "none" },
        }),
  }) as const;

const eventInvoiceStatusLabel = (
  registration: Registration,
  timeZone?: string | null,
): string => getEventSaleInvoiceStatusLabel(registration, timeZone);

const InvoiceManageTicketSalesContent = ({
  attendeeEditTarget = null,
  mode,
  routeTarget,
}: {
  attendeeEditTarget?: EventSalesAttendeeEditTarget | null;
  mode: EventSalesPageMode;
  routeTarget: CmpEventSalesRouteTarget | null;
}): React.ReactElement => {
  const setPageHeaderOverride = usePageHeaderOverride();
  const authenticatedUser = useGetUserFromUserIdInAuth();
  const chamber = authenticatedUser?.chamberInfo;
  const chamberId = chamber?.id || "";
  const canEmailSavedEventDocuments = canManageEventInvoiceFinancialActions({
    enabledPagePermissions:
      authenticatedUser?.usagePermissions?.chamberMemberPro_EnabledPages || [],
    invoiceKind: CmpInvoiceKindEnum.EventRegistration,
    userType: authenticatedUser?.userType,
  });
  const pageTitle =
    mode === "create" ? "Create Event Ticket Invoice" : "Manage Ticket Sales";
  const pageDescription =
    mode === "create"
      ? "Complete the information below to manually sell tickets to a member or non-member. Complimentary tickets will show zero balance on invoice but is required in order to track and assign the ticket."
      : "Manually manage all tickets sales for events.";

  const [selectedEventId, setSelectedEventId] = useState("");
  const [pendingSelectedEventId, setPendingSelectedEventId] = useState<
    string | null
  >(null);

  useEffect(() => {
    setPageHeaderOverride({
      ...invoicePageLayoutPresentation,
      title: pageTitle,
      description: pageDescription,
      withHeaderDivider: true,
    });
    return () => setPageHeaderOverride(null);
  }, [pageDescription, pageTitle, setPageHeaderOverride]);

  // ----- Queries -------------------------------------------------------------
  const { data: eventsData, loading: eventsLoading } =
    useGetCmpManageEventsQuery({
      variables: { input: { chamberId } },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const events = useMemo(
    () => eventsData?.getCmpManageEvents.events || [],
    [eventsData],
  );

  // A linked event is selected once, so clearing the event after Create
  // (design D:10057-10073) does not re-select it.
  const routeEventAppliedRef = useRef<string | null>(null);
  useEffect(() => {
    const focusedEventId = routeTarget?.eventId;

    if (
      focusedEventId &&
      routeEventAppliedRef.current !== focusedEventId &&
      events.some((event) => event.id === focusedEventId)
    ) {
      routeEventAppliedRef.current = focusedEventId;
      setSelectedEventId(focusedEventId);
      setFieldErrors((current) => {
        if (!current.event) {
          return current;
        }
        const next = { ...current };
        delete next.event;
        return next;
      });
    }
  }, [events, routeTarget?.eventId]);

  // Check-in Attendees "Update Attendee Details" (IF11 EV15) opens this page
  // on the event with the registration's editor open.
  const attendeeEditEventAppliedRef = useRef(false);
  useEffect(() => {
    const eventId = attendeeEditTarget?.eventId;
    if (
      mode !== "manage" ||
      routeTarget ||
      !eventId ||
      attendeeEditEventAppliedRef.current ||
      !events.some((event) => event.id === eventId)
    ) {
      return;
    }
    attendeeEditEventAppliedRef.current = true;
    setSelectedEventId(eventId);
  }, [attendeeEditTarget?.eventId, events, mode, routeTarget]);

  const selectedEventPaymentRestriction =
    events.find((event) => event.id === selectedEventId)
      ?.paymentActionRequiredReason || null;

  const { data: eventDetailData, refetch: refetchEventDetail } = useGetCmpEventDetailQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const loadedEvent = eventDetailData?.getCmpEventDetail.event;
  const eventDetail = loadedEvent?.id === selectedEventId ? loadedEvent : null;
  const eventEligibility = getStaffTicketEventEligibility(eventDetail);
  const [closedEventAcknowledgment, setClosedEventAcknowledgment] = useState<{ id: string; revision: number } | null>(null);
  const [closedEventWarning, setClosedEventWarning] = useState(false);
  const [billingMode, setBillingMode] = useState<MemberBillingMode>("member");

  useEffect(() => {
    if (mode === "create" && eventEligibility.closed && eventDetail &&
      (closedEventAcknowledgment?.id !== eventDetail.id || closedEventAcknowledgment.revision !== eventDetail.updatedAtUtcMilli)) {
      setClosedEventWarning(true);
    }
  }, [mode, eventEligibility.closed, eventDetail, closedEventAcknowledgment]);
  const ticketTypes = useMemo(() => eventDetail?.tickets || [], [eventDetail]);
  // Design D:13823: a non-member invoice never offers member-only or
  // sponsor-included tickets (the backend rejects them).
  const invoiceTicketTypes = useMemo(
    () =>
      billingMode === "non-member"
        ? ticketTypes.filter((ticketType) => isNonMemberInvoiceTicketType(ticketType.audience))
        : ticketTypes,
    [billingMode, ticketTypes],
  );
  const choiceFieldsByTicketTypeId = useMemo(
    () =>
      Object.fromEntries(
        ticketTypes.map((ticketType) => [
          ticketType.id,
          getEventAttendeeChoiceFields({
            customFields: eventDetail?.customFields || [],
            ticketTypeId: ticketType.id,
          }),
        ]),
      ),
    [eventDetail, ticketTypes],
  );

  const { readRegistrations } = useEventSalesReadback();
  const {
    data: registrationsData,
    loading: registrationsLoading,
    refetch: refetchRegistrations,
  } = useGetCmpEventRegistrationsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const registrations = useMemo(
    () => registrationsData?.getCmpEventRegistrations.registrations || [],
    [registrationsData],
  );

  const { data: sponsorshipsData } = useGetCmpEventSponsorshipRequestsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId,
    fetchPolicy: "cache-and-network",
  });
  const sponsorships = useMemo(
    () =>
      sponsorshipsData?.getCmpEventSponsorshipRequests.sponsorshipRequests ||
      [],
    [sponsorshipsData],
  );

  // Debounced server-side member search (EVT-BLAIR-SALES-002) — the backend
  // filters on `search` and the limit is raised to 1000 per contract B4.
  // MemberBillingSelector owns the (single) debounce and hands us the trimmed
  // term (the former page-level 300ms double-debounce was removed in the
  // B8/B9 consolidation pass).
  const [memberSearch, setMemberSearch] = useState("");

  const { data: lookupData, loading: lookupsLoading } =
    useGetCmpInvoiceCreateLookupsQuery({
      variables: {
        input: {
          chamberId,
          limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT,
          search: memberSearch || undefined,
          // This page uses the members only.
          ...MEMBER_ONLY_INVOICE_LOOKUP_PARTS,
        },
      },
      skip: !chamberId,
      fetchPolicy: "cache-and-network",
    });
  const members: MemberLookupOption[] = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData],
  );
  const { data: settingsData } = useCmpGlobalSettings(chamberId);
  const chamberTimeZone =
    settingsData?.getChamberMemberProSettings.setup.timeZone;
  const invoicePaymentTerms =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .invoicePaymentTerms;
  // The preview shows the payment columns the issued invoice will show.
  const draftPaymentMethods = useMemo(
    () =>
      getStaffInvoiceDraftPaymentMethods({
        creditCardPaymentsReady: getCmpCreditCardPaymentsReady(
          settingsData?.getChamberMemberProSettings.paymentsAndTaxes
            .stripeConnect,
        ),
        manualPaymentMethods:
          settingsData?.getChamberMemberProSettings.paymentsAndTaxes
            .manualPaymentMethods || [],
      }),
    [settingsData],
  );
  // Tax label + rate come from chamber settings, including split-rate chambers.
  const eventTicketTaxSettings =
    settingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .invoiceTaxSettings?.eventTicketsInvoices;
  const taxRateLabel = getConfiguredInvoiceTaxRateLabel(eventTicketTaxSettings);
  const settingsTaxRatePercentage = getConfiguredInvoiceTaxRatePercentage(
    eventTicketTaxSettings,
  );
  // ----- Mutations -----------------------------------------------------------
  const [createRegistration, { loading: creating }] =
    useCreateCmpEventRegistrationMutation({
      context: eventSalesRegistrationCreateApolloContext,
    });
  const [recordPayment, { loading: recordingPayment }] =
    useRecordCmpEventRegistrationManualPaymentMutation({
      context: eventSalesHostedCheckoutApolloContext,
    });
  const [updateRegistration, { loading: updatingRegistration }] =
    useUpdateCmpEventRegistrationMutation();
  const [exportAttendees, { loading: exportingAttendees }] =
    useExportCmpEventAttendeesCsvLazyQuery({
      context: eventSalesExportApolloContext,
    });
  const [emailCmpInvoiceDocument, { loading: emailingInvoiceDocument }] =
    useEmailCmpInvoiceDocumentMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const { downloadInvoicePdf, downloadingPdf } =
    useDownloadEventInvoicePdf(chamberId);

  // ----- Analytics -----------------------------------------------------------
  // Saved ticket drafts: counted in the cards only (IF11 Q8, option B).
  const { data: ticketDraftsData } = useGetCmpEventTicketInvoiceDraftsQuery({
    variables: { input: { chamberId, eventId: selectedEventId } },
    skip: !chamberId || !selectedEventId || mode !== "manage",
    fetchPolicy: "cache-and-network",
  });
  const analyticsCards = useMemo(() => {
    const drafts = ticketDraftsData?.getCmpEventTicketInvoiceDrafts.drafts || [];
    return getEventTicketStatsCards({
      draftAttendeeCount: getEventTicketDraftAttendeeCount({
        draftForms: drafts.map((draft) => draft.draftForm),
        ticketTypes: eventDetail?.tickets || [],
      }),
      maxAttendeeCapacity: eventDetail?.maxAttendeeCapacity,
      registrations: registrations.map((registration) => ({
        paymentStatus: registration.paymentStatus,
        reservedAttendeeCount: getEventRegistrationReservedAttendeeCount(registration),
      })),
      revenueLabel: formatCurrencyFromCents(eventDetail?.ticketRevenueInCents || 0),
      sponsorships: sponsorships.map((sponsorship) => ({
        attendeeCount: getEventSponsorshipAttendeeCount(sponsorship),
        status: sponsorship.status,
      })),
    });
  }, [registrations, sponsorships, eventDetail, ticketDraftsData]);

  // ----- Create invoice form -------------------------------------------------
  const [billing, setBilling] = useState<BillingDetails>(blankBillingDetails());
  const [ticketLines, setTicketLines] = useState<EventTicketInvoiceLine[]>([
    createEventTicketInvoiceLine(),
  ]);
  const memberAttendeeSuggestions = useEventMemberAttendeeSuggestions({
    chamberId,
    memberId: billingMode === "member" ? billing.selectedMemberCompanyId : null,
  });
  const attendeeSuggestions = useMemo(
    () =>
      mergeEventAttendeeSuggestions(
        memberAttendeeSuggestions,
        getEventAttendeeSuggestions({
          chamberId,
          eventId: selectedEventId,
          registrations,
          selectedMemberCompanyId: billing.selectedMemberCompanyId,
          sponsorships,
        }),
      ),
    [
      billing.selectedMemberCompanyId,
      chamberId,
      memberAttendeeSuggestions,
      registrations,
      selectedEventId,
      sponsorships,
    ],
  );
  const attendeeCompanyOptions = useMemo(
    () => getEventAttendeeCompanyOptions(members),
    [members],
  );
  // Design (11) "Tax Exempt" for this customer (Northern Corridor, Monday
  // 13162938262). The backend charges no tax and prints the note.
  const [taxExempt, setTaxExempt] = useState(false);
  const [taxExemptionNumber, setTaxExemptionNumber] = useState("");
  const [initialInvoiceDates] = useState(() =>
    getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    }),
  );
  const [invoiceDate, setInvoiceDate] = useState(
    initialInvoiceDates.invoiceDate,
  );
  const [dueDate, setDueDate] = useState(initialInvoiceDates.dueDate);
  const [referencePurchaseOrder, setReferencePurchaseOrder] = useState("");
  const [createFormBaseline, setCreateFormBaseline] =
    useState<TicketInvoiceFormDirtyState>(() =>
      getTicketInvoiceFormDirtyState({
        billing,
        dueDate,
        invoiceDate,
        referencePurchaseOrder,
        taxExempt,
        taxExemptionNumber,
        ticketLines,
      }),
    );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pendingCreate, setPendingCreate] = useState(false);
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  // Monday D30: Save Draft and reopening a saved draft (?draftInvoiceId=).
  const navigate = useNavigate();
  const eventInvoiceDraftRoute = useEventInvoiceDraftFromRoute({
    chamberId,
    enabled: mode === "create",
  });
  const [activeDraft, setActiveDraft] = useState<{
    id: string;
    updatedAtUtcMilli: number;
  } | null>(null);
  const [draftSavedCustomerName, setDraftSavedCustomerName] = useState<
    string | null
  >(null);
  const draftHydrationRef = useRef<{
    id: string;
    form: TicketInvoiceDraftForm;
    linesApplied: boolean;
  } | null>(null);
  const [saveEventInvoiceDraft, { loading: savingDraft }] =
    useSaveCmpEventInvoiceDraftMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const [createdInvoice, setCreatedInvoice] = useState<{
    complimentary: boolean;
    eventId: string;
    invoiceId: string;
    recipientEmail: string | null;
    registrationId: string;
  } | null>(null);
  const [createdInvoiceActionError, setCreatedInvoiceActionError] = useState<
    string | null
  >(null);
  const createInvoiceInFlightRef = useRef(false);
  const [successMessage, setSuccessMessage] =
    useState<EventSalesSuccessMessage | null>(null);
  const [paymentSuccess, setPaymentSuccess] =
    useState<EventPaymentSuccessDetails | null>(null);
  const [pendingDocumentEmail, setPendingDocumentEmail] =
    useState<PendingEventInvoiceDocumentEmail | null>(null);
  const [emailSuccessMessage, setEmailSuccessMessage] = useState<string | null>(
    null,
  );
  const paymentSuccessInvoiceId = paymentSuccess?.invoiceId.trim() || null;
  const { data: paymentSuccessInvoiceDetailData } = useGetCmpInvoiceDetailQuery(
    {
      fetchPolicy: "network-only",
      skip: !chamberId || !paymentSuccessInvoiceId,
      variables: {
        input: {
          chamberId,
          invoiceId: paymentSuccessInvoiceId || "CMP_SKIPPED",
        },
      },
    },
  );
  const paymentSuccessInvoiceDetail =
    paymentSuccessInvoiceDetailData?.getCmpInvoiceDetail || null;
  const canEmailPaymentSuccessReceipt = Boolean(
    paymentSuccess &&
    paymentSuccessInvoiceDetail?.invoice.id === paymentSuccess.invoiceId &&
    paymentSuccessInvoiceDetail.availableActions.some(
      ({ action, available }) =>
        action === CmpInvoiceActionTypeEnum.EmailDocument && available,
    ) &&
    paymentSuccessInvoiceDetail.payments.some(
      (payment) =>
        payment.id === paymentSuccess.paymentId &&
        isReceiptEligiblePaymentStatus(payment.status),
    ),
  );
  // API errors surface in a popup modal (EVT-SALES-T-003) — never a header
  // toast.
  const [errorModal, setErrorModal] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const taxRatePercentage = settingsTaxRatePercentage;

  // A saved draft named in the address that cannot open shows why, instead
  // of /500 or a silent empty form (audit 3.1 item 4).
  const draftRouteError = eventInvoiceDraftRoute.error;
  useEffect(() => {
    if (!draftRouteError) return;
    setErrorModal({
      title: "Draft not opened",
      message: getUserFacingErrorText(
        draftRouteError,
        buildStaffErrorFallback("open the saved draft"),
      ),
    });
  }, [draftRouteError]);

  useEffect(() => {
    // A reopened draft keeps its saved dates.
    if (activeDraft) return;
    if (
      !shouldRebaseEventInvoiceDateDefaults({
        baseline: createFormBaseline,
        current: { dueDate, invoiceDate },
      })
    ) {
      return;
    }

    const defaults = getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    });
    if (defaults.invoiceDate === invoiceDate && defaults.dueDate === dueDate) {
      return;
    }

    setInvoiceDate(defaults.invoiceDate);
    setDueDate(defaults.dueDate);
    setCreateFormBaseline((current) => ({
      ...current,
      dueDate: defaults.dueDate,
      invoiceDate: defaults.invoiceDate,
    }));
  }, [
    activeDraft,
    chamberTimeZone,
    createFormBaseline,
    dueDate,
    invoiceDate,
    invoicePaymentTerms,
  ]);

  // Keep line state valid when the selected event changes.
  useEffect(() => {
    setTicketLines((current) =>
      current.map((line) =>
        line.ticketTypeId &&
        !ticketTypes.some((ticketType) => ticketType.id === line.ticketTypeId)
          ? { ...line, attendees: [], ticketTypeId: "" }
          : line,
      ),
    );
  }, [ticketTypes]);

  // Reopen a saved draft: the form first, then the ticket lines once the
  // draft event's ticket types are loaded (the effect above clears lines
  // whose ticket type is not loaded).
  useEffect(() => {
    const draft = eventInvoiceDraftRoute.draft;
    if (!draft || draftHydrationRef.current?.id === draft.id) return;
    const form = parseEventInvoiceDraftForm<TicketInvoiceDraftForm>(
      draft.eventInvoiceDraftForm,
    );
    if (!form || !form.eventId) {
      setErrorModal({
        title: "The draft could not be opened",
        message:
          EVENT_DRAFT_NOT_READ_TEXT,
      });
      return;
    }
    draftHydrationRef.current = { id: draft.id, form, linesApplied: false };
    setActiveDraft({ id: draft.id, updatedAtUtcMilli: draft.updatedAtUtcMilli || 0 });
    setSelectedEventId(form.eventId);
    setBillingMode(form.billingMode);
    setBilling(form.billing);
    setInvoiceDate(form.invoiceDate);
    setDueDate(form.dueDate);
    setReferencePurchaseOrder(form.referencePurchaseOrder);
    setTaxExempt(form.taxExempt);
    setTaxExemptionNumber(form.taxExemptionNumber);
  }, [eventInvoiceDraftRoute.draft]);

  useEffect(() => {
    const pending = draftHydrationRef.current;
    if (!pending || pending.linesApplied || eventDetail?.id !== pending.form.eventId) {
      return;
    }
    pending.linesApplied = true;
    const { form } = pending;
    setTicketLines(form.ticketLines);
    setCreateFormBaseline(
      getTicketInvoiceFormDirtyState({
        billingMode: form.billingMode,
        billing: form.billing,
        dueDate: form.dueDate,
        invoiceDate: form.invoiceDate,
        referencePurchaseOrder: form.referencePurchaseOrder,
        taxExempt: form.taxExempt,
        taxExemptionNumber: form.taxExemptionNumber,
        ticketLines: form.ticketLines,
      }),
    );
    // View/Approve Invoice opens the preview.
    if (eventInvoiceDraftRoute.openPreview) setShowDraftPreview(true);
  }, [eventDetail?.id, eventInvoiceDraftRoute.openPreview, ticketTypes]);

  const ticketLinePricing = useMemo(
    () =>
      ticketLines.map((line) => {
        const ticketType = ticketTypes.find(
          (ticket) => ticket.id === line.ticketTypeId,
        );
        const pricing = getEventTicketLinePricing({
          catalogUnitPriceInCents: ticketType?.priceInCents || 0,
          line,
        });

        return {
          ...pricing,
          line,
          subtotalInCents: ticketType ? pricing.subtotalInCents : 0,
          ticketType,
        };
      }),
    [ticketLines, ticketTypes],
  );
  const invoiceSubtotalInCents = ticketLinePricing.reduce(
    (sum, line) => sum + line.subtotalInCents,
    0,
  );
  const invoiceTaxInCents = getEventInvoiceTaxAmountInCents({
    subtotalInCents: getEventTicketTaxableSubtotalInCents(
      ticketLinePricing.map((line) => ({
        noTax: line.ticketType?.noTax,
        subtotalInCents: line.subtotalInCents,
      })),
    ),
    taxExempt,
    taxRatePercentage: settingsTaxRatePercentage,
  });
  const invoiceTotalInCents = invoiceSubtotalInCents + invoiceTaxInCents;
  // Design (11) TAX column and totals: chamber tax label and rate, "Exempt"
  // for a tax exempt customer, "No Tax" when ticket tax is off.
  const ticketTaxCharged = settingsTaxRatePercentage > 0;
  const ticketTaxRateText = `${taxRateLabel} (${settingsTaxRatePercentage}%)`;
  const ticketTaxPill = !ticketTaxCharged
    ? { label: "No Tax", exempt: false }
    : taxExempt
      ? { label: "Exempt", exempt: true }
      : { label: `${taxRateLabel} ${settingsTaxRatePercentage}%`, exempt: false };
  const ticketTaxTotalLabel = !ticketTaxCharged
    ? "Tax (not charged)"
    : taxExempt
      ? `${ticketTaxRateText} \u2013 Tax Exempt`
      : ticketTaxRateText;
  const ticketTaxExemptNote =
    ticketTaxCharged && taxExempt
      ? `Customer is tax exempt${
          taxExemptionNumber.trim()
            ? ` (Tax Exemption #: ${taxExemptionNumber.trim()})`
            : ""
        }. Tax has not been charged on this invoice.`
      : null;
  const ticketTaxNotAppliedNote =
    ticketTaxCharged && !taxExempt
      ? getEventTicketTaxNotAppliedNote(
          ticketLinePricing
            .filter((line) => line.ticketType?.noTax)
            .map((line) => line.ticketType?.name || ""),
        )
      : null;

  const clearFieldErrors = (...keys: string[]) =>
    setFieldErrors((current) => {
      if (!keys.some((key) => current[key])) {
        return current;
      }
      const next = { ...current };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const handleBillingChange = (next: BillingDetails) => {
    setBilling(next);
  };

  const resetCreateForm = () => {
    const nextBilling = blankBillingDetails();
    const nextTicketLines = [createEventTicketInvoiceLine()];
    const defaults = getDefaultEventInvoiceDates({
      invoicePaymentTerms,
      timeZone: chamberTimeZone,
    });

    setBilling(nextBilling);
    setBillingMode("member");
    setTicketLines(nextTicketLines);
    setInvoiceDate(defaults.invoiceDate);
    setDueDate(defaults.dueDate);
    setReferencePurchaseOrder("");
    setTaxExempt(false);
    setTaxExemptionNumber("");
    setCreateFormBaseline(
      getTicketInvoiceFormDirtyState({
        billing: nextBilling,
        dueDate: defaults.dueDate,
        invoiceDate: defaults.invoiceDate,
        referencePurchaseOrder: "",
        taxExempt: false,
        taxExemptionNumber: "",
        ticketLines: nextTicketLines,
      }),
    );
    setFieldErrors({});
  };

  const validateCreate = (): Record<string, string> => {
    const errors: Record<string, string> = validateEventSalesBilling({
      billing,
      companyNameError: "Customer / Company Name is required",
      requireCompanyName: billingMode === "non-member",
    });
    if (!selectedEventId) {
      errors.event = "Please select an event.";
    }
    if (billingMode === "member" && !billing.selectedMemberCompanyId) {
      errors.member = "Member is required";
    }
    // "Please select an event." (design) stays when no event is selected.
    if (selectedEventId && !eventDetail) errors.event = "Wait for the selected event to load.";
    // Sold To shows before an event is chosen, so this shows on the event field.
    // One text per cause; the rule itself is unchanged (Karen, Oct 19 2026).
    const nonMemberBlockCause = billingMode === "non-member"
      ? getStaffTicketNonMemberBlockCause(eventDetail)
      : null;
    if (eventDetail && nonMemberBlockCause) {
      errors.event = getStaffTicketNonMemberBlockedMessage({
        cause: nonMemberBlockCause,
        eventName: eventDetail.title,
      });
    }
    if (eventEligibility.closed && (closedEventAcknowledgment?.id !== eventDetail?.id ||
      closedEventAcknowledgment?.revision !== eventDetail?.updatedAtUtcMilli)) {
      errors.event = "This event is already closed. Select Yes in the Event Already Closed message to create an invoice for a member.";
      setClosedEventWarning(true);
    }
    if (!invoiceDate) {
      errors.invoiceDate = "Invoice Date is required";
    } else if (!isEventInvoiceDateValid(invoiceDate)) {
      errors.invoiceDate = DATE_FORMAT_MESSAGE;
    }
    if (!dueDate) {
      errors.dueDate = "Due Date is required";
    } else if (!isEventInvoiceDateValid(dueDate)) {
      errors.dueDate = DATE_FORMAT_MESSAGE;
    } else if (!isEventInvoiceDateRangeValid({ dueDate, invoiceDate })) {
      errors.dueDate = "Due Date must be on or after Invoice Date.";
    }
    if (ticketLines.length === 0) {
      errors.ticketLines = "Add at least one ticket type.";
    }
    if (hasDuplicateEventTicketTypes(ticketLines)) {
      errors.ticketLines = "Each ticket type can be selected only once.";
    }

    const primaryAttendeeId = getEventInvoicePrimaryAttendeeId(
      ticketLines.map((line) => line.attendees),
    );
    ticketLines.forEach((line) => {
      if (!line.ticketTypeId) {
        errors[`ticketType.${line.id}`] = "Select a ticket type.";
        if (!errors.ticketLines) {
          errors.ticketLines = eventTicketTypeRequiredMessage;
        }
      }
      if (line.quantity < 1) {
        errors[`quantity.${line.id}`] = "Enter a quantity of at least 1.";
      }
      if (
        line.discountMode === "$"
          ? parseEventTicketDiscountAmountInCents(line.discountAmount) == null
          : parseEventTicketDiscountPercentage(line.discountPercentage) == null
      ) {
        errors[`discount.${line.id}`] =
          line.discountMode === "$"
            ? "Enter a dollar discount, for example 25.00."
            : "Enter a discount from 0.0 to 100.0.";
      }
      if (line.attendees.length === 0) {
        errors[`attendees.${line.id}`] = eventInvoiceAttendeesRequiredMessage;
      }
      Object.assign(
        errors,
        validateEventInvoiceAttendeeFields({
          attendees: line.attendees,
          emailRequiredAttendeeId: primaryAttendeeId,
          errorKeyPrefix: `attendees.${line.id}`,
        }),
      );
    });

    return errors;
  };

  const handleOpenDraftPreview = () => {
    const errors = validateCreate();
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      setShowDraftPreview(true);
      return;
    }
    scrollToFirstInvalidField();
  };

  const handleCreateInvoice = async () => {
    await runEventSalesSingleFlight({
      inFlightGuard: createInvoiceInFlightRef,
      execute: async () => {
        const errors = validateCreate();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
          scrollToFirstInvalidField();
          return;
        }

        const purchaser = splitName(
          billing.billingContactName || billing.customerName,
        );
        const ticketInvoiceLines = ticketLinePricing.map((line) => ({
          discountPercentage: line.discountPercentage,
          ...(line.discountAmountInCents !== null
            ? { discountAmountInCents: line.discountAmountInCents }
            : {}),
          quantity: line.line.quantity,
          ticketTypeId: line.line.ticketTypeId,
        }));
        const attendeeInputs = ticketLines.flatMap((line) =>
          line.attendees.map((attendee) => {
            const attendeeName = splitName(attendee.name);

            return {
              customFieldAnswers:
                getEventInvoiceAttendeeCustomFieldAnswerInputs({ attendee }),
              email: attendee.email.trim(),
              firstName: attendeeName.first,
              lastName: attendeeName.last || undefined,
              phone: attendee.phone.trim() || undefined,
              companyName: attendee.companyName?.trim() || undefined,
              staffNotes: attendee.staffNotes?.trim() || undefined,
              ticketTypeId: line.ticketTypeId,
            };
          }),
        );
        const invoiceDetails = {
          dueDate,
          invoiceDate,
          referencePurchaseOrder:
            normalizeEventInvoiceReference(referencePurchaseOrder) || undefined,
        };

        setPendingCreate(true);
        try {
          const result = await runEventSalesOperation({
            action: "CREATE_REGISTRATION",
            chamberId,
            entityId: "new-registration",
            eventId: selectedEventId,
            input: {
              chamberId,
              eventId: selectedEventId,
              ...(activeDraft ? {
                draftInvoiceId: activeDraft.id,
                draftExpectedUpdatedAtUtcMilli: activeDraft.updatedAtUtcMilli,
              } : {}),
              expectedEventUpdatedAtUtcMilli: eventDetail?.updatedAtUtcMilli,
              ...(eventEligibility.closed && closedEventAcknowledgment ? {
                closedEventAcknowledgedAtRevision: closedEventAcknowledgment.revision,
              } : {}),
              purchaserFirstName: purchaser.first || billing.customerName,
              purchaserLastName: purchaser.last || "-",
              purchaserEmail: billing.billingEmail,
              purchaserPhone: billing.billingPhone || undefined,
              selectedMemberCompanyId:
                billing.selectedMemberCompanyId || undefined,
              selectedMemberCompanyNameSnapshot:
                billing.customerName || undefined,
              billing: toCmpEventBillingInput(billing),
              invoiceDetails,
              ticketInvoiceLines,
              attendees: attendeeInputs,
              invoiceTaxExempt: taxExempt,
              invoiceTaxExemptionNumber:
                taxExempt && taxExemptionNumber.trim()
                  ? taxExemptionNumber.trim()
                  : undefined,
              treatAsComplimentary: false,
            },
            execute: (input) =>
              createRegistration({
                variables: { input },
              }),
            isAuthoritativelyCompleted: (mutationResult) => {
              const createPayload =
                mutationResult.data?.createCmpEventRegistration;

              return Boolean(
                createPayload?.registrationSavedSuccessfully &&
                createPayload.registration?.id &&
                createPayload.registration.invoice?.id,
              );
            },
          });
          const payload = result.data?.createCmpEventRegistration;
          const registration = payload?.registration;

          if (
            !payload?.registrationSavedSuccessfully ||
            !registration?.invoice?.id
          ) {
            // The invoice may exist, so staff check before a retry.
            throw new UserFacingError(
              "We could not confirm that the ticket invoice was created. Check Events & Sponsorships > Manage Tickets before you try again.",
            );
          }

          const createdEventId = selectedEventId;
          // Design D:10057-10073: a created invoice resets the whole form,
          // including Select Event, and returns to the top. Dates restart at
          // today and the chamber payment terms (Sept 24 rule).
          resetCreateForm();
          setSelectedEventId("");
          if (activeDraft) {
            setActiveDraft(null);
            draftHydrationRef.current = null;
            navigate(RoutePaths.chamberAdmin_events_ticketSalesCreate, { replace: true });
          }
          scrollDashboardToTop();
          setCreatedInvoice({
            complimentary: isComplimentaryStatus(registration.paymentStatus),
            eventId: createdEventId,
            invoiceId: registration.invoice.id,
            recipientEmail: getSavedInvoiceRecipientEmail(registration.invoice),
            registrationId: registration.id,
          });
          setCreatedInvoiceActionError(
            await refreshAfterConfirmedSave({
              refresh: () =>
                readRegistrations({ chamberId, eventId: createdEventId }),
              savedWhat: "The ticket invoice was created",
            }),
          );
        } catch (error) {
          const createFallback = buildStaffErrorFallback("create the ticket invoice");
          const message = getUserFacingErrorText(error, createFallback);
          // Route by cmpErrorReason first. The old backend texts stay as a
          // fallback until the backend release is live on both sides.
          const serverText = getGraphQLErrorMessage(error) || "";
          const reason = getCmpErrorReason(error);
          const blockCause = getStaffTicketNonMemberBlockCauseFromReason(reason);
          const isEventRefusal = Boolean(blockCause) ||
            reason === CMP_STAFF_TICKET_REFUSAL_REASONS.closedEventConfirmRequired ||
            serverText.includes("Event Already Closed") ||
            serverText.includes("Non-member ticket invoices");

          if (isEventRefusal) {
            setClosedEventAcknowledgment(null);
            await refetchEventDetail();
            setFieldErrors((current) => ({
              ...current,
              // The server sentence names the event. A cause without one
              // uses the same client text.
              event: blockCause && message === createFallback
                ? getStaffTicketNonMemberBlockedMessage({ cause: blockCause, eventName: eventDetail?.title })
                : message,
            }));
            return;
          }
          if (
            hasGraphQLErrorCode(error, CMP_EVENT_INVENTORY_CAPACITY_UNAVAILABLE_CODE) ||
            /(capacity|sold out|available|remaining|inventory)/i.test(serverText)
          ) {
            setFieldErrors((current) => ({
              ...current,
              ticketLines: message,
            }));
            return;
          }

          setErrorModal({
            title: "Ticket invoice not created",
            message,
          });
        } finally {
          setPendingCreate(false);
        }
      },
    });
  };

  // ----- Manage list: filters ------------------------------------------------
  const [search, setSearch] = useState(routeTarget?.invoiceNumber || "");
  const [invoiceStatusFilters, setInvoiceStatusFilters] = useState<
    EventInvoiceSelectableStatusFilter[]
  >([]);
  const [ticketTypeFilters, setTicketTypeFilters] = useState<string[]>([]);
  const [invoiceStatusMenuAnchor, setInvoiceStatusMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [ticketTypeMenuAnchor, setTicketTypeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [sort, setSort] = useState<EventSalesSortState<SortColumn> | null>(
    null,
  );

  useEffect(() => {
    if (routeTarget?.invoiceNumber) {
      setSearch(routeTarget.invoiceNumber);
      setInvoiceStatusFilters([]);
    }
  }, [routeTarget?.invoiceNumber]);

  const clearTicketFilters = () => {
    setSearch("");
    setInvoiceStatusFilters([]);
    setTicketTypeFilters([]);
  };

  const sortedTicketRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = getEventTicketSalesRows({ registrations, sponsorships, ticketTypes }).filter(row => {
      const haystack = [row.source.invoice?.invoiceNumber, row.attendeeName, row.companyName,
        row.source.purchaserFirstName, row.source.purchaserLastName, row.source.purchaserEmail].join(" ").toLowerCase();
      return (!term || routeTarget?.sourceId === row.source.id || haystack.includes(term)) &&
        eventSaleMatchesStatusFilters(row.source, invoiceStatusFilters, chamberTimeZone) &&
        (!ticketTypeFilters.length || row.attendees.some(attendee => ticketTypeFilters.includes(attendee.ticketTypeId)));
    });
    if (!sort) return rows;
    if (sort.column === "invoiceNumber") return sortRowsByInvoiceNumber({ rows, direction: sort.direction,
      getInvoiceNumber: row => row.source.invoice?.invoiceNumber || "" });
    const value = (row: typeof rows[number]): string | number => {
      switch (sort.column) {
        case "tickets": return row.count;
        case "invoiceStatus": return getEventSaleInvoiceStatusLabel(row.source, chamberTimeZone);
        case "ticketStatus": return isPaidStatus(row.source.paymentStatus) ? "Issued" : "Allocated";
        case "attendeeName": return row.attendeeName.toLowerCase();
        case "companyName": return row.companyName.toLowerCase();
        case "ticketType": return row.ticketType.toLowerCase();
        default: return "";
      }
    };
    return [...rows].sort((a, b) => (value(a) < value(b) ? -1 : value(a) > value(b) ? 1 : 0) * (sort.direction === "asc" ? 1 : -1));
  }, [registrations, sponsorships, ticketTypes, search, routeTarget?.sourceId, invoiceStatusFilters, ticketTypeFilters, chamberTimeZone, sort]);

  // Shared functional sort header (lifted to eventSalesShared so B9's
  // decorative affordances became real in the same pass).
  const toggleSort = (column: SortColumn) =>
    setSort((current) => toggleEventSalesSort(current, column));

  const renderSortableHeader = (label: string, column: SortColumn) => (
    <EventSalesSortableHeader
      label={label}
      column={column}
      sort={sort}
      onToggle={toggleSort}
    />
  );

  // ----- Process payment (Open Invoice) --------------------------------------
  const [paymentTarget, setPaymentTarget] = useState<Registration | null>(null);
  const [paymentConfirmationTarget, setPaymentConfirmationTarget] =
    useState<Registration | null>(null);
  const [documentTarget, setDocumentTarget] = useState<Registration | null>(
    null,
  );
  const [documentModeOverride, setDocumentModeOverride] = useState<
    "INVOICE" | "PAYMENT_RECEIPT" | null
  >(null);
  const currentPaymentTarget = useMemo(
    () =>
      paymentTarget
        ? registrations.find(
            (registration) => registration.id === paymentTarget.id,
          ) || paymentTarget
        : null,
    [paymentTarget, registrations],
  );
  const currentPaymentRules = currentPaymentTarget?.invoice?.paymentRules;
  const { recordableMethods: staffRecordablePaymentMethods } =
    useStaffRecordablePaymentMethods({
      chamberId,
      skip: !currentPaymentTarget,
    });
  const currentManualPaymentMethods = useMemo(
    () =>
      getStaffPaymentMethodOptions({
        paymentRules: currentPaymentRules,
        recordableMethods: staffRecordablePaymentMethods,
      }),
    [currentPaymentRules, staffRecordablePaymentMethods],
  );
  const currentPaymentUnavailableReason =
    currentPaymentTarget?.paymentActionUnavailableReason ||
    selectedEventPaymentRestriction ||
    (!currentPaymentRules
      ? "We could not check which payment options are available. Refresh the page and try again."
      : null);
  const currentDocumentTarget = useMemo(
    () =>
      documentTarget
        ? registrations.find(
            (registration) => registration.id === documentTarget.id,
          ) || documentTarget
        : null,
    [documentTarget, registrations],
  );
  const openInvoice = (registration: Registration) => {
    setDocumentModeOverride(null);
    if (registration.invoice) {
      setDocumentTarget(registration);
    }
  };

  const handleRecordPayment = async (payload: ProcessPaymentSubmit) => {
    if (!paymentTarget || !paymentTarget.invoice) {
      return;
    }
    try {
      const result = await runEventSalesOperation({
        action: "RECORD_REGISTRATION_MANUAL_PAYMENT",
        chamberId,
        entityId: paymentTarget.id,
        eventId: selectedEventId,
        input: {
          chamberId,
          eventId: selectedEventId,
          registrationId: paymentTarget.id,
          invoiceId: paymentTarget.invoice.id,
          amountInCents: payload.amountInCents,
          manualPaymentMethodId: payload.manualPaymentMethodId,
          receivedAtUtcMilli: payload.receivedAtUtcMilli,
          reference: payload.reference || undefined,
          note: payload.note || undefined,
        },
        execute: (input) =>
          recordPayment({
            variables: { input },
          }),
        isAuthoritativelyCompleted: (mutationResult) => {
          const paymentResult =
            mutationResult.data?.recordCmpEventRegistrationManualPayment;

          return Boolean(
            paymentResult?.registrationSavedSuccessfully &&
            paymentResult.registration?.id === paymentTarget.id,
          );
        },
      });
      const savedRegistration =
        result.data?.recordCmpEventRegistrationManualPayment.registration;
      if (
        !result.data?.recordCmpEventRegistrationManualPayment
          .registrationSavedSuccessfully ||
        savedRegistration?.id !== paymentTarget.id
      ) {
        throw new Error("The registration payment could not be confirmed.");
      }
      const successDetails = getEventPaymentSuccessDetails(
        savedRegistration,
        paymentTarget.billingSnapshot?.customerName ||
          paymentTarget.selectedMemberCompanyNameSnapshot ||
          [paymentTarget.purchaserFirstName, paymentTarget.purchaserLastName]
            .filter(Boolean)
            .join(" ") ||
          "Customer",
      );
      if (!successDetails) {
        // The payment is saved; only the receipt is missing. The pop-up
        // stays open as before, with a title that says the payment worked.
        setErrorModal({
          title: "Payment recorded",
          message:
            "The payment was recorded, but we could not show the receipt. Do not record it again. Refresh the page and check the invoice status.",
        });
        return;
      }
      setPaymentTarget(null);
      setPaymentSuccess(successDetails);
      const refreshWarning = await refreshAfterConfirmedSave({
        refresh: () =>
          readRegistrations({ chamberId, eventId: selectedEventId }),
        savedWhat: "The payment was recorded",
      });
      if (refreshWarning) {
        setErrorModal({ title: "Payment recorded", message: refreshWarning });
      }
    } catch (error) {
      // An unknown result may have saved, so staff check before a retry.
      setErrorModal({
        title: "Payment not confirmed",
        message: getUserFacingErrorText(
          error,
          buildStaffCheckFirstFallback(
            "confirm the payment",
            "Refresh the page and check the invoice status",
          ),
        ),
      });
    }
  };

  // ----- Update attendee names -----------------------------------------------
  const [editTarget, setEditTarget] = useState<Registration | null>(null);
  // Update Attendee Details lists the edited sale's member's people (IF11 EV7).
  const editAttendeeSuggestions = useEventMemberAttendeeSuggestions({
    chamberId,
    memberId: editTarget?.selectedMemberCompanyId,
  });
  const [editAttendees, setEditAttendees] = useState<
    EventSalesAttendeeNameEdit[]
  >([]);
  // Update Attendee Details groups attendees by ticket type (design groups).
  const editTicketGroups = useMemo(
    () =>
      editTarget
        ? getEventAttendeeTicketGroupHeaders({
            lines: getEventRegistrationTicketTypeLines({
              registration: editTarget,
              ticketTypes,
            }),
            ticketTypes,
          })
        : undefined,
    [editTarget, ticketTypes],
  );

  const openEditAttendees = (registration: Registration) => {
    setEditTarget(registration);
    setEditAttendees(
      registration.attendees.map((attendee) => ({
        customFieldAnswers: Object.fromEntries(
          attendee.customFieldAnswers.map(({ fieldId, value }) => [
            fieldId,
            value,
          ]),
        ),
        email: attendee.email || "",
        id: attendee.id,
        firstName: attendee.firstName,
        lastName: attendee.lastName || "",
        phone: attendee.phone || "",
        companyName: attendee.companyName || "",
        staffNotes: attendee.staffNotes || "",
        ticketTypeId: attendee.ticketTypeId,
      })),
    );
  };

  const attendeeEditOpenedRef = useRef(false);
  useEffect(() => {
    if (
      mode !== "manage" ||
      !attendeeEditTarget ||
      attendeeEditOpenedRef.current ||
      selectedEventId !== attendeeEditTarget.eventId
    ) {
      return;
    }
    const registration = registrations.find(
      (candidate) =>
        candidate.id === attendeeEditTarget.sourceId &&
        candidate.eventId === attendeeEditTarget.eventId,
    );
    if (!registration) {
      return;
    }
    attendeeEditOpenedRef.current = true;
    openEditAttendees(registration);
    // openEditAttendees only sets local editor state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendeeEditTarget, mode, registrations, selectedEventId]);

  const handleSaveAttendeeNames = async () => {
    if (!editTarget) return;
    try {
      const input = {
        chamberId,
        eventId: selectedEventId,
        registrationId: editTarget.id,
        purchaserFirstName: editTarget.purchaserFirstName,
        purchaserLastName: editTarget.purchaserLastName,
        purchaserEmail: editTarget.purchaserEmail,
        purchaserPhone: editTarget.purchaserPhone || undefined,
        selectedMemberCompanyId:
          editTarget.selectedMemberCompanyId || undefined,
        selectedMemberCompanyNameSnapshot:
          editTarget.selectedMemberCompanyNameSnapshot || undefined,
        billing: toCmpEventBillingInputFromSnapshot(
          editTarget.billingSnapshot || editTarget.invoice?.billingSnapshot,
        ),
        status: editTarget.status,
        attendees: buildCmpEventAttendeeNameUpdateInputs({
          attendees: editTarget.attendees,
          edits: editAttendees,
        }),
        customFieldAnswers: toCmpEventCustomFieldAnswerInputs({
          answers: editTarget.customFieldAnswers,
        }),
      };
      // Claude Design (21) never offers Delete in Update Attendee Details
      // (CMP Dashboard.dc.html:18797 canDelete false); complimentary tickets are
      // cancelled with Void Invoice in Manage all Invoices (Monday 13314268710).
      const requiresGuardedUpdate =
        editTarget.attendees.some(({ status }) => status === CmpEventRegistrationStatusEnum.Canceled);
      const prepared = requiresGuardedUpdate ? await prepareEventSalesOperation({
        action: "UPDATE_REGISTRATION_ATTENDEES", chamberId, eventId: selectedEventId,
        entityId: editTarget.id,
        input: buildGuardedEventAttendeeUpdateInput({
          input, deletedAttendeeIds: [], expectedUpdatedAtUtcMilli: editTarget.updatedAtUtcMilli,
        }),
      }) : null;
      const result = await updateRegistration({
        context: eventSalesCallerHandledApolloContext,
        variables: { input: prepared?.input || input },
      });
      const updateResult = result.data?.updateCmpEventRegistration;
      if (
        !updateResult?.registrationSavedSuccessfully ||
        updateResult.registration?.id !== editTarget.id ||
        updateResult.registration.chamberId !== chamberId ||
        updateResult.registration.eventId !== selectedEventId
      ) {
        throw new Error("The attendee update could not be confirmed.");
      }
      await refetchRegistrations();
      if (prepared) completeEventSalesOperation({ operation: prepared });
      setEditTarget(null);
      setSuccessMessage({
        message: eventSalesChangesSavedMessage,
        highlights: [],
      });
    } catch (error) {
      throw error instanceof Error
        ? error
        : new Error("Could not update attendee details.");
    }
  };

  // ----- Export --------------------------------------------------------------
  const handleExportAttendees = async () => {
    if (!selectedEventId) {
      return;
    }
    try {
      downloadEventAttendeeListCsv(
        await exportAttendees({
          variables: { input: { chamberId, eventId: selectedEventId } },
        }),
      );
      setSuccessMessage(getEventAttendeeListExportSuccess());
    } catch (error) {
      setErrorModal({
        title: "Attendee list not exported",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback("export the attendee list"),
        ),
      });
    }
  };

  // Export Filter/Selected Attendee List (D:4583, 9960-9976). The table has
  // no row selection, so this exports the rows the current filters show.
  const hasActiveTicketFilters =
    Boolean(search.trim()) ||
    invoiceStatusFilters.length > 0 ||
    ticketTypeFilters.length > 0;
  const handleExportFilteredAttendees = () => {
    if (!hasActiveTicketFilters) {
      return;
    }
    try {
      downloadCsvFile({
        csvContent: buildEventFilteredAttendeeCsv(
          sortedTicketRows.map((row) => ({
            attendeeName: row.attendeeName,
            companyName: row.companyName,
            invoiceNumber: row.source.invoice?.invoiceNumber || "",
            invoiceStatus: getEventSaleInvoiceStatusLabel(
              row.source,
              chamberTimeZone,
            ),
            quantity: row.count,
            ticketType: formatEventTicketTypeLinesForCsv(row.ticketTypeLines),
          })),
        ),
        contentType: "text/csv;charset=utf-8",
        filename: eventFilteredAttendeeListCsvFilename,
      });
      setSuccessMessage(getEventFilteredAttendeeListExportSuccess());
    } catch (error) {
      setErrorModal({
        title: "Attendee list not exported",
        message: getUserFacingErrorText(
          error,
          buildStaffErrorFallback("export the filtered attendee list"),
        ),
      });
    }
  };

  const selectedEvent = events.find((event) => event.id === selectedEventId);
  const eventName = selectedEvent?.title || "Event";

  // Compare the full current form with the last confirmed baseline. This
  // makes change-then-restore clean and keeps ordered lines significant.
  const currentCreateFormState = getTicketInvoiceFormDirtyState({
    billingMode,
    billing,
    dueDate,
    invoiceDate,
    referencePurchaseOrder,
    taxExempt,
    taxExemptionNumber,
    ticketLines,
  });
  const createFormDirty = hasUnsavedFormChanges({
    baseline: createFormBaseline,
    current: currentCreateFormState,
  });

  // The preview document lines; Save Draft stores the same lines.
  const ticketDraftDocumentLines = getEventTicketInvoiceDocumentLines(
    ticketLinePricing.flatMap((line) =>
      line.ticketType
        ? [{
            catalogUnitPriceInCents: line.ticketType.priceInCents,
            code: line.ticketType.code,
            discountAmountInCents: line.discountAmountInCents,
            discountPercentage: line.discountPercentage,
            id: line.line.id,
            name: line.ticketType.name || "Ticket",
            quantity: line.line.quantity,
          }]
        : [],
    ),
    getEventInvoiceDisplayName(selectedEvent),
  );

  // Design (15) "Save Draft" (Monday D30): the form becomes a Draft invoice
  // in Manage All Invoices; no attendees or tickets are held yet.
  const handleSaveDraft = async () => {
    setShowDraftPreview(false);
    await runEventSalesSingleFlight({
      inFlightGuard: createInvoiceInFlightRef,
      execute: async () => {
        const errors = validateCreate();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
          scrollToFirstInvalidField();
          return;
        }
        const customerName = billing.customerName.trim() || "the member";
        try {
          const result = await runEventSalesOperation({
            action: "SAVE_INVOICE_DRAFT",
            chamberId,
            entityId: activeDraft?.id || "new-ticket-invoice-draft",
            eventId: selectedEventId,
            input: {
              chamberId,
              ...(activeDraft ? {
                invoiceId: activeDraft.id,
                expectedUpdatedAtUtcMilli: activeDraft.updatedAtUtcMilli,
              } : {}),
              sourceKind: CmpOperationalSourceKindEnum.EventRegistration,
              eventId: selectedEventId,
              chamberMemberId: billing.selectedMemberCompanyId || undefined,
              billingSnapshot: {
                customerName: billing.customerName,
                contactName: billing.billingContactName || undefined,
                contactEmail: billing.billingEmail,
                contactPhone: billing.billingPhone || undefined,
                addressLine1: billing.streetAddress || undefined,
                addressLine2: billing.addressLine2 || undefined,
                city: billing.city || undefined,
                province: billing.province || undefined,
                postalCode: billing.postalCode || undefined,
                country: billing.country || undefined,
              },
              invoiceDetails: {
                dueDate,
                invoiceDate,
                referencePurchaseOrder:
                  normalizeEventInvoiceReference(referencePurchaseOrder) || undefined,
              },
              lineItems: toEventInvoiceDraftLineInputs(ticketDraftDocumentLines),
              taxAmountInCents: invoiceTaxInCents,
              draftForm: serializeEventInvoiceDraftForm({
                eventId: selectedEventId,
                billingMode,
                billing,
                ticketLines,
                invoiceDate,
                dueDate,
                referencePurchaseOrder,
                taxExempt,
                taxExemptionNumber,
              }),
            },
            execute: (input) => saveEventInvoiceDraft({ variables: { input } }),
            isAuthoritativelyCompleted: (mutationResult) =>
              Boolean(mutationResult.data?.saveCmpEventInvoiceDraft.invoiceSavedSuccessfully),
          });
          const saved = result.data?.saveCmpEventInvoiceDraft.invoice;
          if (!saved) throw new Error("The draft could not be confirmed after saving.");
          setActiveDraft({ id: saved.id, updatedAtUtcMilli: saved.updatedAtUtcMilli || 0 });
          setCreateFormBaseline(currentCreateFormState);
          setDraftSavedCustomerName(customerName);
        } catch (error) {
          setErrorModal({
            title: "Draft not saved",
            message: getUserFacingErrorText(
              error,
              buildStaffCheckFirstFallback(
                "confirm that the draft was saved",
                "Refresh the page and check the draft",
              ),
            ),
          });
        }
      },
    });
  };

  const handleSelectedEventChange = (nextEventId: string) => {
    if (
      mode === "create" &&
      shouldConfirmEventSelectionChange({
        currentEventId: selectedEventId,
        formDirty: createFormDirty,
        nextEventId,
      })
    ) {
      setPendingSelectedEventId(nextEventId);
      return;
    }

    setSelectedEventId(nextEventId);
    clearFieldErrors("event");
  };

  const handleConfirmSelectedEventChange = () => {
    if (!pendingSelectedEventId) {
      return;
    }

    const nextEventId = pendingSelectedEventId;
    resetCreateForm();
    setSelectedEventId(nextEventId);
    setPendingSelectedEventId(null);
  };


  return (
    <InvoiceDesignScope>
      <FormPageShell
        headingId="ticket-sales-page"
        heading={pageTitle}
        mainClassName="w-full"
        mainPaddingTopClassName="pt-[18px]"
        contentClassName="space-y-[26px]"
        beforeMain={
          <>
            <ConfirmDialog open={closedEventWarning} title="Event Already Closed"
              message={`The event “${eventDetail?.title || eventName}” is already closed. Do you want to continue and create an invoice for a member?`}
              confirmLabel="Yes" cancelLabel="Cancel"
              onCancel={() => { setClosedEventWarning(false); setSelectedEventId(""); setClosedEventAcknowledgment(null); }}
              onConfirm={() => {
                if (!eventDetail || typeof eventDetail.updatedAtUtcMilli !== "number" || !Number.isSafeInteger(eventDetail.updatedAtUtcMilli)) return;
                setClosedEventAcknowledgment({ id: eventDetail.id, revision: eventDetail.updatedAtUtcMilli });
                setBillingMode("member");
                setClosedEventWarning(false);
                clearFieldErrors("event", "member");
              }} />
            <UnsavedChangesPrompt
              variant="page"
              when={mode === "create" && createFormDirty}
              saving={creating}
            />
            {pendingSelectedEventId ? (
              <UnsavedChangesDialog
                onLeave={handleConfirmSelectedEventChange}
                onStay={() => setPendingSelectedEventId(null)}
              />
            ) : null}
          </>
        }
      >
        <EventSalesSection
          id="select-event"
          variant="plain"
          title="Select Event"
          required={mode === "create"}
          headerClassName="mb-[10px] flex flex-col gap-2"
          descriptionClassName="text-[14px] leading-[18px] text-black"
          titleClassName={
            mode === "create"
              ? "text-[16px] font-semibold leading-[21px] text-black"
              : "text-[17px] font-semibold leading-[22px] text-black"
          }
          description="Search or select the event from the drop down."
        >
          <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-end md:justify-between">
            <div className="w-full md:max-w-[600px] md:flex-1">
              <EventSelect
                error={Boolean(fieldErrors.event)}
                helperText={fieldErrors.event}
                options={events}
                value={selectedEventId}
                onChange={handleSelectedEventChange}
                loading={eventsLoading}
              />
            </div>

          </div>
          {selectedEventPaymentRestriction ? (
            <Alert className="mt-4" severity="warning">
              {/* The reason after the first sentence is server text. */}
              You cannot record payments or change sales for this event right
              now. {selectedEventPaymentRestriction}
            </Alert>
          ) : null}
        </EventSalesSection>

        {mode === "manage" ? (
          <EventSalesSection
            id="live-analytics"
            variant="plain"
            title={`${eventName} Live Analytics`}
            description="View analytics for this event"
          >
            <EventStatCardRow hideIcons cards={analyticsCards} />
          </EventSalesSection>
        ) : null}

        {mode === "create" ? (
          <EventSalesSection
            id="create-event-ticket-invoice"
            showHeader={false}
            title="Create Event Ticket Invoice"
            variant="plain"
            description="Complete the information below to manually sell tickets to a member or non-member. Complimentary tickets will show zero balance on invoice but is required in order to track and assign the ticket."
          >
            <div className="space-y-[26px]">
              <div>
                <MemberBillingSelector
                  members={members}
                  billing={billing}
                  errors={fieldErrors}
                  onChange={handleBillingChange}
                  onClearError={clearFieldErrors}
                  loading={lookupsLoading}
                  mode={billingMode}
                  // Claude Design (18) D:5360: Sold To shows before an event is chosen.
                  onModeChange={(nextMode) => {
                    setBillingMode(nextMode);
                    setBilling((current) => ({ ...current, selectedMemberId: "", selectedMemberCompanyId: "" }));
                    if (nextMode === "non-member") {
                      // Lines holding a member-only type start over.
                      setTicketLines((current) => current.map((line) => {
                        const ticketType = ticketTypes.find((type) => type.id === line.ticketTypeId);
                        return !ticketType || isNonMemberInvoiceTicketType(ticketType.audience)
                          ? line
                          : { ...line, attendees: [], quantity: 0, ticketTypeId: "" };
                      }));
                    }
                    clearFieldErrors("member", "companyName");
                  }}
                  onSearchChange={setMemberSearch}
                  heading="Select Company to Invoice"
                  headingClassName="text-[16px] font-semibold leading-[21px] text-gray-950"
                  helperText="Edit contact, email and phone number, if different than what auto-populates."
                  memberLabelClassName="text-[15px] font-semibold leading-[20px] text-gray-950"
                  billingFieldLabelClassName="text-[14px] font-semibold leading-[18px] text-gray-950"
                  // D:17409 (EV15-79): non-member invoices ask for the address.
                  billingAddressHelperText={
                    billingMode === "non-member"
                      ? "Enter the billing/mailing address for this customer."
                      : "Primary address on file will be selected if billing/mailing address has not been provided. Edit details as required."
                  }
                />
              </div>

              <EventInvoiceCreateFields
                dueDate={dueDate}
                dueDateError={fieldErrors.dueDate}
                headingClassName="text-[20px] font-bold leading-[26px] text-gray-950"
                invoiceDate={invoiceDate}
                invoiceDateError={fieldErrors.invoiceDate}
                onDueDateChange={(value) => {
                  setDueDate(value);
                  clearFieldErrors("dueDate");
                }}
                onInvoiceDateChange={(value) => {
                  setInvoiceDate(value);
                  setDueDate(
                    getEventInvoiceDueDateForIssueDate({
                      invoiceDate: value,
                      invoicePaymentTerms,
                      timeZone: chamberTimeZone,
                    }),
                  );
                  clearFieldErrors("invoiceDate", "dueDate");
                }}
                onReferenceChange={(value) => setReferencePurchaseOrder(value)}
                reference={referencePurchaseOrder}
              />

              <div className="space-y-[26px]">
                <h3 className="text-[20px] font-bold leading-[26px] text-gray-950">
                  Enter Invoice Details
                </h3>
                <EventTicketTaxExemptBox
                  disabled={creating || pendingCreate}
                  exempt={taxExempt}
                  number={taxExemptionNumber}
                  onExemptChange={setTaxExempt}
                  onNumberChange={setTaxExemptionNumber}
                />
                <EventTicketInvoiceLinesEditor
                  attendeeSuggestions={attendeeSuggestions}
                  attendeeCompanyOptions={attendeeCompanyOptions}
                  taxPill={ticketTaxPill}
                  totals={
                    <div className="mb-3 flex justify-end">
                      <dl className="m-0 grid min-w-[300px] grid-cols-[auto_minmax(120px,auto)] gap-x-10 gap-y-2.5 text-[14px] leading-[normal] text-[#0A0A0A]">
                        <dt>Sub-Total</dt>
                        <dd className="m-0 text-right">
                          {formatCurrencyFromCents(invoiceSubtotalInCents)}
                        </dd>
                        <dt>{ticketTaxTotalLabel}</dt>
                        <dd className="m-0 text-right">
                          {formatCurrencyFromCents(invoiceTaxInCents)}
                        </dd>
                        <dt className="border-t border-[#ECECEC] pt-2.5 text-[16px] font-bold">
                          Total
                        </dt>
                        <dd className="m-0 border-t border-[#ECECEC] pt-2.5 text-right text-[16px] font-bold">
                          {formatCurrencyFromCents(invoiceTotalInCents)}
                        </dd>
                      </dl>
                    </div>
                  }
                  choiceFieldsByTicketTypeId={choiceFieldsByTicketTypeId}
                  disabled={creating || pendingCreate}
                  errors={fieldErrors}
                  lines={ticketLines}
                  onChange={setTicketLines}
                  onClearError={clearFieldErrors}
                  ticketTypeLocked={!eventDetail}
                  ticketTypes={invoiceTicketTypes}
                />
              </div>

              {fieldErrors.ticketLines ? (
                <p className="m-0 text-right text-[13px] font-semibold text-[#E24C4C]" role="alert">
                  {fieldErrors.ticketLines}
                </p>
              ) : null}
              <div className="flex flex-wrap items-center justify-end gap-5">
                <PreviewInvoiceButton onClick={handleOpenDraftPreview} />
                <Button
                  variant="contained"
                  disabled={creating}
                  onClick={() => void handleCreateInvoice()}
                  startIcon={
                    creating && pendingCreate ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }
                  sx={createInvoiceButtonSx}
                >
                  Create Invoice
                </Button>
              </div>
            </div>
          </EventSalesSection>
        ) : null}

        {mode === "manage" ? (
          <>
            <EventSalesSection
              id="ticket-sale-filters"
              variant="plain"
              title="Ticket Sale Filters"
              description="Use filters to easily sort information"
            >
              <EventSalesFilterBar
                searchValue={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search by Attendee Name or Company Name"
                searchMinWidth={340}
                filters={[
                  {
                    label: "Filter by Invoice Status",
                    active: invoiceStatusFilters.length > 0,
                    onOpen: setInvoiceStatusMenuAnchor,
                    selectedLabels: invoiceStatusFilters.map(
                      (value) =>
                        eventInvoiceSelectableStatusFilterOptions.find(
                          (option) => option.value === value,
                        )?.label || value,
                    ),
                    width: 240,
                  },
                  {
                    label: "Filter by Ticket Type",
                    active: ticketTypeFilters.length > 0,
                    onOpen: setTicketTypeMenuAnchor,
                    selectedLabels: ticketTypeFilters.map(
                      (value) =>
                        ticketTypes.find((ticket) => ticket.id === value)
                          ?.name || value,
                    ),
                    width: 240,
                  },
                ]}
                onClearAllFilters={clearTicketFilters}
              />

              <EventSalesSearchableMultiSelectMenu
                anchorEl={invoiceStatusMenuAnchor}
                ariaLabel="Invoice status filters"
                onChange={setInvoiceStatusFilters}
                onClose={() => setInvoiceStatusMenuAnchor(null)}
                options={eventInvoiceSelectableStatusFilterOptions}
                searchPlaceholder="Search Invoice Status"
                values={invoiceStatusFilters}
              />
              <EventSalesSearchableMultiSelectMenu
                anchorEl={ticketTypeMenuAnchor}
                ariaLabel="Ticket type filters"
                onChange={setTicketTypeFilters}
                onClose={() => setTicketTypeMenuAnchor(null)}
                options={ticketTypes.map((ticket) => ({
                  label: ticket.name,
                  value: ticket.id,
                }))}
                searchPlaceholder="Search Ticket Type"
                values={ticketTypeFilters}
              />
            </EventSalesSection>

            <EventSalesSection
              id="manage-ticket-list"
              action={
                <div className="flex flex-nowrap justify-end gap-3">
                  <Button
                    variant="contained"
                    startIcon={<FileUploadOutlinedIcon />}
                    disabled={exportingAttendees || !selectedEventId}
                    onClick={handleExportAttendees}
                    sx={[goldButtonSx, { flexShrink: 0, whiteSpace: "nowrap" }]}
                  >
                    Export Attendee List
                  </Button>
                  <Button
                    variant="contained"
                    aria-disabled={!hasActiveTicketFilters}
                    startIcon={<FileUploadOutlinedIcon />}
                    onClick={handleExportFilteredAttendees}
                    sx={[
                      goldButtonSx,
                      eventFilteredExportButtonSx(hasActiveTicketFilters),
                    ]}
                  >
                    Export Filter/Selected Attendee List
                  </Button>
                </div>
              }
              variant="plain"
              title={`Manage ${eventName} Tickets Sales`}
              description="Update the attendee list, view invoices, or open an invoice and apply payment to invoices not paid."
            >
              <TableContainer className="rounded-[10px] border border-[#ECECEC]">
                <Table
                  size="small"
                  sx={{
                    minWidth: 1622,
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      borderColor: "#ECECEC",
                      fontSize: 14,
                      px: 2,
                      py: 1.75,
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontSize: 13,
                      fontWeight: 700,
                      letterSpacing: "normal",
                      lineHeight: "18px",
                      py: 1.625,
                    },
                  }}
                >
                  <colgroup>
                    {[110, 227.3, 272.8, 227.3, 100, 227.3, 227.3, 210].map(
                      (width, index) => (
                        <col key={`${width}-${index}`} style={{ width }} />
                      ),
                    )}
                  </colgroup>
                  <TableHead className="bg-[#F7F7F8]">
                    <TableRow>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Invoice #", "invoiceNumber")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Attendee Name", "attendeeName")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Company Name", "companyName")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Ticket Type", "ticketType")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Ticket Qty", "tickets")}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader(
                          "Invoice Status",
                          "invoiceStatus",
                        )}
                      </TableCell>
                      <TableCell className="!font-bold">
                        {renderSortableHeader("Ticket Status", "ticketStatus")}
                      </TableCell>
                      {/* Actions is not sortable (mock) — no sort affordance. */}
                      <TableCell
                        className="!font-bold"
                        align="center"
                        sx={{
                          ...stickyActionColumnHeaderSx,
                          minWidth: 230,
                          width: 230,
                          ...salesActionCellButtonsSx,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registrationsLoading && registrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" className="!py-8">
                          <CircularProgress size={22} sx={{ color: cpaGold }} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!registrationsLoading &&
                    sortedTicketRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: "0 !important" }}>
                          {selectedEventId ? (
                            <TableEmptyState
                              heading="No tickets match your filters"
                              onClear={clearTicketFilters}
                              clearLabel="Clear All Filter"
                            />
                          ) : (
                            // Design D:4720-4724: before an event is chosen.
                            <TableEmptyState
                              icon="document"
                              heading="No event selected"
                              helper="Select an event from the dropdown above to view its ticket sales and attendees."
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {sortedTicketRows.map((row) => {
                      if (row.sponsorship) {
                        const source = row.sponsorship;
                        const sponsorshipSearch = {
                          eventId: source.eventId,
                          invoiceNumber: source.invoice?.invoiceNumber,
                          sponsorshipId: source.id,
                        };
                        const destination =
                          RoutePaths.chamberAdmin_events_sponsorshipSalesManage +
                          getSponsorshipSalesSearch(sponsorshipSearch);
                        const updateAttendeesDestination =
                          RoutePaths.chamberAdmin_events_sponsorshipSalesManage +
                          getSponsorshipSalesSearch({
                            ...sponsorshipSearch,
                            editAttendees: true,
                          });
                        return <TableRow key={row.key} data-record-id={source.id} data-source-kind="sponsorship">
                          <TableCell>
                            {source.invoice?.invoiceNumber ? (
                              <Link
                                className="font-bold text-gray-950 underline"
                                to={destination}
                              >
                                {source.invoice.invoiceNumber}
                              </Link>
                            ) : isComplimentaryStatus(source.paymentStatus) ? (
                              "-"
                            ) : (
                              "Pending"
                            )}
                          </TableCell>
                          <TableCell>{row.attendeeName}</TableCell><TableCell>{row.companyName || "-"}</TableCell>
                          <TableCell><TicketTypeLinesCell lines={row.ticketTypeLines} /></TableCell><TableCell>{row.count}</TableCell>
                          <TableCell>{getEventSaleInvoiceStatusLabel(source, chamberTimeZone)}</TableCell>
                          <TableCell>{isPaidStatus(source.paymentStatus) ? "Issued" : "Allocated"}</TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...stickyActionColumnCellSx,
                              minWidth: 230,
                              width: 230,
                              ...salesActionCellButtonsSx,
                            }}
                          >
                            <div className="flex flex-col gap-[6px]">
                              <Button
                                component={Link}
                                to={updateAttendeesDestination}
                                size="small"
                                variant="contained"
                                sx={goldButtonSx}
                              >
                                Update Attendee Details
                              </Button>
                              <Button
                                component={Link}
                                to={destination}
                                variant="contained"
                                size="small"
                                sx={blackButtonSx}
                              >
                                View Sponsorship
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>;
                      }
                      const registration = row.registration;
                      const paidInvoice =
                        registration.invoice?.invoiceStatus ===
                        CmpInvoiceStatusEnum.Paid;
                      return (
                        <TableRow
                          data-chamber-id={registration.chamberId}
                          data-event-id={registration.eventId}
                          data-is-cmp-event-module-test-data={registration.isCmpEventModuleTestData === true}
                          data-record-id={registration.id}
                          key={registration.id}
                        >
                          <TableCell>
                            {registration.invoice ? (
                              <button
                                type="button"
                                className="font-bold text-gray-950 underline"
                                onClick={() => openInvoice(registration)}
                              >
                                {registration.invoice.invoiceNumber}
                              </button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>{row.attendeeName}</TableCell>
                          <TableCell>
                            {row.companyName || "-"}
                          </TableCell>
                          <TableCell>
                            <TicketTypeLinesCell lines={row.ticketTypeLines} />
                          </TableCell>
                          <TableCell>
                            {getEventRegistrationTicketCount(registration)}
                          </TableCell>
                          <TableCell>
                            {eventInvoiceStatusLabel(
                              registration,
                              chamberTimeZone,
                            )}
                          </TableCell>
                          <TableCell>
                            {ticketStatusLabel(registration)}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...stickyActionColumnCellSx,
                              minWidth: 230,
                              width: 230,
                              ...salesActionCellButtonsSx,
                            }}
                          >
                            <div className="flex flex-col gap-[6px]">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => openEditAttendees(registration)}
                                sx={goldButtonSx}
                              >
                                Update Attendee Details
                              </Button>
                              {registration.invoice ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => openInvoice(registration)}
                                  sx={blackButtonSx}
                                >
                                  {paidInvoice
                                    ? "View Invoice"
                                    : "Open Invoice"}
                                </Button>
                              ) : routeTarget?.sourceId === registration.id ? (
                                <span className="text-xs font-semibold text-amber-700">
                                  Invoice {routeTarget.invoiceNumber} requires
                                  support reconciliation.
                                </span>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </EventSalesSection>

            {/* Process payment (Open Invoice) */}
            <ManageInvoiceActionConfirmationDialog
              customerName={
                paymentConfirmationTarget?.billingSnapshot?.customerName ||
                paymentConfirmationTarget?.selectedMemberCompanyNameSnapshot ||
                [
                  paymentConfirmationTarget?.purchaserFirstName,
                  paymentConfirmationTarget?.purchaserLastName,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                "Customer"
              }
              kind="PROCESS_PAYMENT"
              onClose={() => setPaymentConfirmationTarget(null)}
              onConfirm={() => {
                const registration = paymentConfirmationTarget;

                if (
                  !registration?.invoice ||
                  registration.invoice.amountDueInCents <= 0 ||
                  registration.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                ) {
                  setPaymentConfirmationTarget(null);
                  setErrorModal({
                    title: "Payment unavailable",
                    message:
                      registration?.paymentActionUnavailableReason ||
                      selectedEventPaymentRestriction ||
                      (registration?.invoice
                        ? EVENT_INVOICE_NOTHING_TO_PAY_TEXT
                        : EVENT_INVOICE_NOT_FOUND_TEXT),
                  });
                  return;
                }

                setPaymentConfirmationTarget(null);
                setPaymentTarget(registration);
              }}
              open={Boolean(paymentConfirmationTarget)}
            />

            <ProcessPaymentModal
              open={Boolean(paymentTarget)}
              customerName={
                currentPaymentTarget
                  ? currentPaymentTarget.selectedMemberCompanyNameSnapshot ||
                    `${currentPaymentTarget.purchaserFirstName} ${currentPaymentTarget.purchaserLastName}`
                  : ""
              }
              invoiceNumber={currentPaymentTarget?.invoice?.invoiceNumber || ""}
              amountDueInCents={
                currentPaymentTarget?.invoice?.amountDueInCents || 0
              }
              invoiceLineItems={currentPaymentTarget?.invoice?.lineItems || []}
              subtotalAmountInCents={
                currentPaymentTarget?.invoice?.subtotalAmountInCents
              }
              taxAmountInCents={currentPaymentTarget?.invoice?.taxAmountInCents}
              totalAmountInCents={
                currentPaymentTarget?.invoice?.totalAmountInCents
              }
              paymentMethods={currentManualPaymentMethods}
              creditCardAvailable={Boolean(
                currentPaymentRules?.creditCardAllowed &&
                !currentPaymentUnavailableReason,
              )}
              paymentActionUnavailableReason={currentPaymentUnavailableReason}
              requireManualPaymentConfirmation={false}
              loading={recordingPayment}
              onSubmit={handleRecordPayment}
              onClose={() => setPaymentTarget(null)}
              renderSecureCardCheckout={({
          amountInCents,
          beforeSubmit,
          cardNote,
          onPaymentInProgressChange,
        }) =>
                currentPaymentTarget?.invoice ? (
                  <EventSecureCardCheckoutPanel
                    onPaymentInProgressChange={onPaymentInProgressChange}
                    key={`registration-checkout:${currentPaymentTarget.id}:${currentPaymentTarget.invoice.id}:${amountInCents}`}
                    amountDueInCents={amountInCents}
                    beforeSubmit={beforeSubmit}
                    cardNote={cardNote}
                    chamberId={chamberId}
                    eventId={selectedEventId}
                    invoiceId={currentPaymentTarget.invoice.id}
                    invoiceStatus={currentPaymentTarget.invoice.invoiceStatus}
                    onPaymentProcessed={async () => {
                      try {
                        const readback = await refetchRegistrations();
                        const savedRegistration =
                          readback.data?.getCmpEventRegistrations.registrations.find(
                            (registration) =>
                              registration.id === currentPaymentTarget.id,
                          );
                        const successDetails = getEventPaymentSuccessDetails(
                          savedRegistration,
                          currentPaymentTarget.billingSnapshot?.customerName ||
                            currentPaymentTarget.selectedMemberCompanyNameSnapshot ||
                            [
                              currentPaymentTarget.purchaserFirstName,
                              currentPaymentTarget.purchaserLastName,
                            ]
                              .filter(Boolean)
                              .join(" ") ||
                            "Customer",
                        );

                        if (!successDetails) {
                          throw new Error(
                            "The saved ticket payment receipt could not be confirmed.",
                          );
                        }

                        setPaymentTarget(null);
                        setPaymentSuccess(successDetails);
                      } catch (error) {
                        // The card payment was processed; only the
                        // receipt read after it did not finish.
                        setErrorModal({
                          title: "Card payment processed",
                          message: eventCardPaymentReceiptUnavailableMessage,
                        });
                      }
                    }}
                    onRefreshAuthoritativeStatus={async () => {
                      const readback = await refetchRegistrations();
                      return getEventSecureCardCheckoutReadback(
                        readback.data?.getCmpEventRegistrations.registrations.find(source => source.id === currentPaymentTarget.id),
                        currentPaymentTarget.invoice!.id,
                      );
                    }}
                    paymentStatus={currentPaymentTarget.paymentStatus}
                    purpose="REGISTRATION"
                    sourceId={currentPaymentTarget.id}
                    sourceStatus={currentPaymentTarget.status}
                  />
                ) : null
              }
            />

            {currentDocumentTarget?.invoice ? (
              <SavedEventInvoiceDocumentModal
                actionLoading={downloadingPdf || emailingInvoiceDocument}
                chamber={chamber}
                chamberId={chamberId}
                customerName={
                  currentDocumentTarget.selectedMemberCompanyNameSnapshot ||
                  `${currentDocumentTarget.purchaserFirstName} ${currentDocumentTarget.purchaserLastName}`.trim()
                }
                documentMode={
                  documentModeOverride ||
                  getEventInvoiceDocumentMode(
                    currentDocumentTarget.invoice.invoiceStatus,
                  ) ||
                  "INVOICE"
                }
                invoiceId={currentDocumentTarget.invoice.id}
                invoiceKind={CmpInvoiceKindEnum.EventRegistration}
                invoiceNumber={currentDocumentTarget.invoice.invoiceNumber}
                recipientEmail={
                  currentDocumentTarget.billingSnapshot?.contactEmail ||
                  currentDocumentTarget.purchaserEmail
                }
                sourceEmailPermission={canEmailSavedEventDocuments}
                onAddPayment={
                  currentDocumentTarget.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                    ? undefined
                    : () => {
                        const registration = currentDocumentTarget;

                        setDocumentTarget(null);
                        setDocumentModeOverride(null);
                        setPaymentConfirmationTarget(registration);
                      }
                }
                onClose={() => {
                  setDocumentTarget(null);
                  setDocumentModeOverride(null);
                }}
                onPrintPdf={() =>
                  downloadInvoicePdf(currentDocumentTarget.invoice?.id)
                }
                paymentActionUnavailableReason={
                  currentDocumentTarget.paymentActionUnavailableReason ||
                  selectedEventPaymentRestriction
                }
                onEmail={(documentMode) => {
                  const invoiceId =
                    currentDocumentTarget.invoice?.id.trim() || "";

                  if (!invoiceId) {
                    setErrorModal({
                      title: "Invoice not emailed",
                      message:
                        "We could not find the saved ticket invoice. Refresh the page and try again.",
                    });
                    return;
                  }

                  const customerName =
                    currentDocumentTarget.billingSnapshot?.customerName ||
                    currentDocumentTarget.selectedMemberCompanyNameSnapshot ||
                    `${currentDocumentTarget.purchaserFirstName} ${currentDocumentTarget.purchaserLastName}`.trim() ||
                    "Customer";

                  setPendingDocumentEmail({
                    customerName,
                    invoiceId,
                    invoiceLabel: `${currentDocumentTarget.invoice?.invoiceNumber || "Ticket invoice"} email`,
                    mode:
                      documentMode === "PAYMENT_RECEIPT"
                        ? CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
                        : CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
                    paymentId:
                      documentMode === "PAYMENT_RECEIPT"
                        ? currentDocumentTarget.paymentId
                        : undefined,
                    recipientEmail:
                      currentDocumentTarget.billingSnapshot?.contactEmail ||
                      currentDocumentTarget.purchaserEmail,
                  });
                }}
                open={Boolean(documentTarget)}
              />
            ) : null}

            {/* Update attendee names (shared modal, "labeled" variant preserves
          this page's existing DOM) */}
            <UpdateAttendeeNamesModal
              companyOptions={attendeeCompanyOptions}
              nameOptions={editAttendeeSuggestions}
              choiceFieldsForAttendee={(attendee) =>
                getEventAttendeeChoiceFields({
                  customFields: eventDetail?.customFields || [],
                  ticketTypeId: attendee.ticketTypeId,
                })
              }
              open={Boolean(editTarget)}
              title="Update Attendee Details"
              description={getEventAttendeeUpdateHelperText({
                awaitingPayment: Boolean(
                  editTarget && !isPaidStatus(editTarget.paymentStatus),
                ),
                kind: "ticket",
              })}
              attendees={editAttendees.filter(({ id }) =>
                editTarget && editTarget.attendees.some((attendee) => attendee.id === id && attendee.status !== CmpEventRegistrationStatusEnum.Canceled),
              )}
              onAttendeeChange={(id, patch) =>
                setEditAttendees((current) =>
                  current.map((item) =>
                    item.id === id ? { ...item, ...patch } : item,
                  ),
                )
              }
              onClose={() => setEditTarget(null)}
              onSave={handleSaveAttendeeNames}
              saving={updatingRegistration}
              ticketGroups={editTicketGroups}
              variant="labeled"
              fieldSx={goldFieldSx}
            />
          </>
        ) : null}

        <EventInvoiceDraftPreview
          members={members}
          open={mode === "create" && showDraftPreview}
          billing={billing}
          chamber={chamber}
          creating={(creating && pendingCreate) || savingDraft}
          onCreate={() => {
            setShowDraftPreview(false);
            void handleCreateInvoice();
          }}
          onSaveDraft={handleSaveDraft}
          onClose={() => setShowDraftPreview(false)}
          invoiceKind={CmpInvoiceKindEnum.EventRegistration}
          eventCode={selectedEvent?.eventCode}
          eventName={eventName}
          eventStartAtUtcMilli={selectedEvent?.startAtUtcMilli}
          eventTimeZone={eventDetail?.timeZone || chamberTimeZone}
          dueDate={dueDate}
          invoiceDate={invoiceDate}
          manualPaymentMethods={draftPaymentMethods}
          lineItems={ticketDraftDocumentLines}
          publicMemo={ticketTaxExemptNote || ticketTaxNotAppliedNote}
          referencePurchaseOrder={normalizeEventInvoiceReference(
            referencePurchaseOrder,
          )}
          subtotalInCents={invoiceSubtotalInCents}
          taxInCents={invoiceTaxInCents}
          taxLabel={`${taxRateLabel} (${taxRatePercentage}%)`}
          taxRegistrationNumber={
            settingsData?.getChamberMemberProSettings.paymentsAndTaxes
              .taxRegistrationNumber
          }
          totalInCents={invoiceTotalInCents}
        />

        <ActionSuccessDialog
          message={successMessage?.message}
          highlights={successMessage?.highlights}
          onClose={() => setSuccessMessage(null)}
        />

        <ActionSuccessDialog
          highlights={draftSavedCustomerName ? [draftSavedCustomerName] : undefined}
          message={draftSavedCustomerName ? getEventInvoiceDraftSavedMessage(draftSavedCustomerName) : null}
          onClose={() => {
            setDraftSavedCustomerName(null);
            navigate(RoutePaths.chamberAdmin_invoicing_manage);
          }}
        />

        <PaymentProcessedDialog
          actionLoading={emailingInvoiceDocument}
          billingContactEmail={paymentSuccess?.billingContactEmail}
          customerName={paymentSuccess?.customerName || "Customer"}
          onClose={() => setPaymentSuccess(null)}
          onPrintReceipt={
            paymentSuccess &&
            registrations.some(
              (registration) =>
                registration.invoice?.id === paymentSuccess.invoiceId &&
                registration.paymentId === paymentSuccess.paymentId,
            )
              ? () => {
                  const savedRegistration = registrations.find(
                    (registration) =>
                      registration.invoice?.id === paymentSuccess.invoiceId &&
                      registration.paymentId === paymentSuccess.paymentId,
                  );

                  if (savedRegistration) {
                    setPaymentSuccess(null);
                    setDocumentModeOverride("PAYMENT_RECEIPT");
                    setDocumentTarget(savedRegistration);
                  }
                }
              : undefined
          }
          onEmailReceipt={
            paymentSuccess && canEmailPaymentSuccessReceipt
              ? () => {
                  const receipt = paymentSuccess;

                  setPaymentSuccess(null);
                  setPendingDocumentEmail({
                    customerName: receipt.customerName,
                    invoiceId: receipt.invoiceId,
                    invoiceLabel: "Ticket payment receipt email",
                    mode: CmpInvoiceDocumentEmailModeEnum.PaymentReceipt,
                    paymentId: receipt.paymentId,
                    recipientEmail: receipt.billingContactEmail,
                  });
                }
              : undefined
          }
          open={Boolean(paymentSuccess)}
        />

        <ManageInvoiceActionConfirmationDialog
          customerName={pendingDocumentEmail?.customerName || "Customer"}
          kind={
            pendingDocumentEmail?.mode ===
            CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
              ? "EMAIL_RECEIPT"
              : "EMAIL_INVOICE"
          }
          loading={emailingInvoiceDocument}
          onClose={() => setPendingDocumentEmail(null)}
          onConfirm={async () => {
            const pendingEmail = pendingDocumentEmail;

            if (!pendingEmail) {
              return;
            }

            const emailResult = await emailSavedEventInvoiceDocument({
              chamberId,
              execute: (input) =>
                emailCmpInvoiceDocument({ variables: { input } }),
              invoiceId: pendingEmail.invoiceId,
              invoiceLabel: pendingEmail.invoiceLabel,
              mode: pendingEmail.mode,
              paymentId: pendingEmail.paymentId,
            });
            setEmailSuccessMessage(
              pendingEmail.mode ===
                CmpInvoiceDocumentEmailModeEnum.PaymentReceipt
                ? `Receipt emailed successfully to ${emailResult.toEmail} for ${pendingEmail.customerName}!`
                : `Email sent successfully to ${emailResult.toEmail} for ${pendingEmail.customerName}!`,
            );
            setPendingDocumentEmail(null);
          }}
          open={Boolean(pendingDocumentEmail)}
          recipientEmail={pendingDocumentEmail?.recipientEmail}
        />

        <InvoiceSuccessDialog
          message={emailSuccessMessage || ""}
          onClose={() => setEmailSuccessMessage(null)}
          open={Boolean(emailSuccessMessage)}
        />

        <InvoiceCreatedDialog
          actionError={createdInvoiceActionError}
          message={
            createdInvoice?.complimentary
              ? "Ticket Invoice has been generated for internal use."
              : "Ticket Invoice has been generated, would you like to print or email the invoice?"
          }
          onClose={() => {
            setCreatedInvoice(null);
            setCreatedInvoiceActionError(null);
          }}
          emailing={emailingInvoiceDocument}
          onEmail={
            createdInvoice && !createdInvoice.complimentary
              ? async () => {
                  try {
                    setCreatedInvoiceActionError(null);
                    const emailResult = await emailSavedEventInvoiceDocument({
                      chamberId,
                      execute: (input) =>
                        emailCmpInvoiceDocument({ variables: { input } }),
                      invoiceId: createdInvoice.invoiceId,
                      invoiceLabel: "Ticket invoice email",
                      mode: CmpInvoiceDocumentEmailModeEnum.InvoiceDocument,
                    });

                    return {
                      actualRecipientEmail: emailResult.toEmail,
                      successMessage: emailResult.successMessage,
                    };
                  } catch (error) {
                    setCreatedInvoiceActionError(
                      getUserFacingErrorText(
                        error,
                        buildStaffCheckFirstFallback(
                          "confirm that the ticket invoice was emailed",
                          "Refresh the page and check the invoice",
                        ),
                      ),
                    );
                    throw error;
                  }
                }
              : undefined
          }
          onPrint={async () => {
            if (!createdInvoice) {
              return;
            }

            try {
              setCreatedInvoiceActionError(null);
              await downloadInvoicePdf(createdInvoice.invoiceId);
            } catch (error) {
              setCreatedInvoiceActionError(
                getUserFacingErrorText(
                  error,
                  buildStaffErrorFallback("make the ticket invoice PDF"),
                ),
              );
            }
          }}
          open={Boolean(createdInvoice)}
          printing={downloadingPdf}
          recipientEmail={createdInvoice?.recipientEmail}
          title={
            createdInvoice?.complimentary
              ? "Complimentary Ticket Invoice Created"
              : "Ticket Invoice Created"
          }
        />

        {/* API errors surface here as a popup, never a header toast. */}
        <ResultModal
          open={Boolean(errorModal)}
          success={false}
          title={errorModal?.title || ""}
          message={errorModal?.message}
          onClose={() => setErrorModal(null)}
        />
      </FormPageShell>
    </InvoiceDesignScope>
  );
};

type InvoiceManageTicketSalesProps = Readonly<{
  mode: EventSalesPageMode;
}>;

const InvoiceManageTicketSales = ({
  mode,
}: InvoiceManageTicketSalesProps): React.ReactElement => {
  const location = useLocation();
  const pixelQaState = getDashboardPixelQaState(
    location.search,
    "ticket-sales",
  );

  if (pixelQaState) {
    return <EventDashboardPixelQa state={pixelQaState} />;
  }

  return (
    <InvoiceManageTicketSalesContent
      attendeeEditTarget={getEventSalesAttendeeEditTarget(location.search)}
      mode={mode}
      routeTarget={getCmpEventSalesRouteTarget(location.search)}
    />
  );
};

export default InvoiceManageTicketSales;
